import React, { useContext, useState, useEffect, useRef } from "react";
import UserBubbleStack from "../../components/UserBubbleStack";
import { AppContext } from "../../context/app.context";
import { renderFlagsWithIcons } from "../../utility/renderFlagsWithIcons";

const JobTile = ({
  project,
  projectDetailClick,
  screenName,
  dragStarted,
  className = "",
  style = {},
  largeView,
  draggable: draggableProp = true,
  selectable = false,
  projectSelect,
  isParkedOriginal = false,
  onParkJob,
  // SUP-9005 — optional Lock/Unlock item shown in the SAME right-click menu as
  // Park, so the lock feature (#174, a cell-level right-click) no longer
  // shadows Park on scheduled job tiles. Shape: { label, disabled, title,
  // onClick } — built by the caller (optimizerScreen) via buildLockMenuItem so
  // it matches the cell menu exactly. When provided, a tile right-click shows a
  // combined menu and stops the event from also opening the cell's lock menu.
  // Omitted everywhere except assigned Optimizer cells → other usages unchanged.
  lockMenuItem = null,
  // Time Machine #114 Phase 6 — Diff Pair view. When non-null, the project
  // differs between T1 and T2; render a highlight border + stacked username
  // bubbles per change event in the (T1, T2] window. `diff` shape:
  //   { id, changeKind: "added"|"removed"|"changed", events: [...], leftDoc, rightDoc }
  // Null/undefined in single-grid mode (live, time-travel, or read-only Phase 5).
  diff = null,
  // Iter 6: time-travel banner mute-highlights checkbox dims the bubbles
  // and softens the border so the user can investigate something else
  // without diff visuals competing for attention.
  diffMuted = false,
}) => {
  // Time Machine #114 Phase 4 read-only invariant: NO tile may be
  // draggable while time-traveling, regardless of what the call site
  // passed for `draggable`. Centralized override here so every JobTile
  // invocation across OptimizerScreen + ScheduleScreen is covered with
  // a single change. Without this, the call sites that pass
  // `draggable=true` (e.g. renderUnassignedScheduledCells) let the user
  // pick up tiles in time-travel — they couldn't actually mutate data
  // (processDragNDrop early-returns and the mutation hooks throw), but
  // the visual still implies edit-ability, which is alarming on PROD
  // data. Force false here.
  const { isTimeTraveling, setOpenAttributeHistoryModal } =
    useContext(AppContext) || {};
  const draggable = isTimeTraveling ? false : draggableProp;

  // Phase 6 — when time-traveling, clicking an attribute pseudo-job opens
  // the AttributeHistoryModal for that (payrollNo, date) cell. The
  // attribute pseudo-job carries the formatted payrollNo on `installer`
  // and the schedule-grid date on `start` (both populated by
  // schedule.context.js's buildScheduleArrayForWeek). Outside time-travel
  // there's no click handler on attribute tiles (was the case before this
  // commit too) — adding it here doesn't change live-mode behavior.
  const handleAttributeClick = (e) => {
    if (!isTimeTraveling) return;
    if (typeof setOpenAttributeHistoryModal !== "function") return;
    const payrollNo = project?.installer || "";
    const date = project?.start || "";
    if (payrollNo === "" || date === "") return;
    e.stopPropagation();
    setOpenAttributeHistoryModal({ show: true, payrollNo, date });
  };
  // Context menu state for parking
  const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0 });
  const contextMenuRef = useRef(null);

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(event.target)) {
        setContextMenu({ visible: false, x: 0, y: 0 });
      }
    };
    if (contextMenu.visible) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [contextMenu.visible]);

  // Handle right-click to show park context menu
  //
  // Time Machine #114 #119 read-only invariant: NEVER show the park menu
  // during time-travel. Park is a client-side mutation (parkedJobs state +
  // localStorage); the parked status persists AFTER exiting time-travel,
  // which violates the Phase 4 promise that historical-view actions don't
  // affect live state. Belt-and-suspenders: this guard suppresses the menu
  // surface, and handleParkClick below has a defensive check in case the
  // menu is somehow already open when isTimeTraveling flips on.
  const parkAvailable = Boolean(onParkJob) && !isParkedOriginal;
  const handleContextMenu = (event) => {
    if (isTimeTraveling) return;
    // Show the menu if EITHER action applies: Park (parkable tile) or
    // Lock/Unlock (SUP-9005 — passed in for assigned Optimizer cells).
    if (parkAvailable || lockMenuItem) {
      event.preventDefault();
      // SUP-9005 — stop the event bubbling to the CELL's lock-menu handler;
      // otherwise both menus open and the cell menu shadows this one (which is
      // exactly the bug Brandon reported). The lock option is included HERE
      // instead, so nothing is lost.
      event.stopPropagation();
      setContextMenu({ visible: true, x: event.clientX, y: event.clientY });
    }
  };

  // Handle park action from context menu
  const handleParkClick = () => {
    // #119 defensive guard — see handleContextMenu above.
    if (isTimeTraveling) {
      setContextMenu({ visible: false, x: 0, y: 0 });
      return;
    }
    if (onParkJob) {
      onParkJob(project);
    }
    setContextMenu({ visible: false, x: 0, y: 0 });
  };

  // SUP-9005 — Lock/Unlock action from the combined context menu.
  const handleLockClick = () => {
    if (
      lockMenuItem &&
      !lockMenuItem.disabled &&
      typeof lockMenuItem.onClick === "function"
    ) {
      lockMenuItem.onClick();
    }
    setContextMenu({ visible: false, x: 0, y: 0 });
  };
  const firstSpaceIndex = project?.encodedSmallTitle?.indexOf(" ");

  // add tooltips to emoji icons
  var tooltipText = "";
  tooltipText = project?.encodedSmallTitle?.includes("📦")
    ? `📦 expressShip\n`
    : tooltipText;
  tooltipText = project?.encodedSmallTitle?.includes("🪑")
    ? tooltipText + `🪑 furniture\n`
    : tooltipText;
  tooltipText = project?.encodedSmallTitle?.includes("📅")
    ? tooltipText + `📅 prescheduled\n`
    : tooltipText;
  tooltipText = project?.encodedSmallTitle?.includes("⚡️")
    ? tooltipText + `⚡️ rush\n`
    : tooltipText;
  tooltipText = project?.encodedSmallTitle?.includes("🏃🏽‍♂️")
    ? tooltipText + `🏃🏽‍♂️ expedite\n`
    : tooltipText;
  tooltipText = project?.encodedSmallTitle?.includes("✈️")
    ? tooltipText + `✈️ distant\n`
    : tooltipText;
  tooltipText = project?.encodedSmallTitle?.includes("🛑")
    ? tooltipText + `🛑 IMS-warning\n`
    : tooltipText;

  return (
    <>
      {project?.extraCell ? (
        <div
          key={project.id}
          data-testid="job-tile"
          onClick={(event) =>
            selectable
              ? projectSelect(project, event)
              : projectDetailClick(project, event)
          }
          className={`text-sm-2 min-h-6 px-2 flex bg-attribute-data rounded relative ${
            project.selected && "border-[bg-skin-base-hover]"
          } ${className}`}
        >
          {selectable && (
            <div
              className="border border-transparent hover:border-[rgb(var(--color-fill-base))] rounded-full w-[20px] h-[20px] flex items-center justify-center absolute top-1 right-1 transition-colors"
              title="Details"
              onClick={(event) => {
                event.stopPropagation();
                return projectDetailClick(project, event);
              }}
            >
              <svg
                viewBox="0 0 16 16"
                xmlns="http://www.w3.org/2000/svg"
                className="w-[16px] h-[16px] fill-skin-base"
              >
                <g id="SVGRepo_bgCarrier" strokewidth="0"></g>
                <g
                  id="SVGRepo_tracerCarrier"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                ></g>
                <g id="SVGRepo_iconCarrier">
                  <path d="M9.5 13a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0z"></path>
                </g>
              </svg>
            </div>
          )}
          <span
            className={`self-center font-medium ${
              screenName === "optimizer" ? "" : "text-black"
            }`}
          >
            {
              // 2025-02-03 - added text-black to this span to force darkMode extraCell text to be black, Scrum request
              largeView
                ? project?.encodedLargeTitle
                : project?.encodedSmallTitle
            }
          </span>
        </div>
      ) : project?.attribute ? (
        <div
          key={project.id}
          data-testid="attribute-cell"
          data-payroll-no={project?.installer || ""}
          data-date={project?.start || ""}
          onClick={isTimeTraveling ? handleAttributeClick : undefined}
          className={`text-sm-2 min-h-6 px-2 flex bg-attribute-data rounded relative ${
            isTimeTraveling ? "cursor-pointer hover:opacity-80" : ""
          } ${className}`}
          style={
            diff
              ? {
                  outline: `2px solid ${diff.changeKind === "removed" ? "#7f1d1d" : "#dc2626"}`,
                  outlineOffset: "-2px",
                  opacity: diffMuted ? 0.65 : 1,
                }
              : undefined
          }
          title={
            isTimeTraveling
              ? "Click to see this attribute cell's full change history"
              : undefined
          }
        >
          <span className="self-center font-medium text-black">
            {
              // 2025-02-03 - added text-black to this span to force darkMode extraCell text to be black, Scrum request
              largeView
                ? project?.encodedLargeTitle
                : project?.encodedSmallTitle
            }
          </span>
          {diff && diff.events && diff.events.length > 0 && (
            <UserBubbleStack events={diff.events} muted={diffMuted} />
          )}
        </div>
      ) : (
        <div
          key={project?.id}
          data-testid="job-tile"
          draggable={isParkedOriginal ? false : draggable}
          onClick={(event) =>
            selectable
              ? projectSelect(project, event)
              : projectDetailClick(project, event)
          }
          onDragStart={() => !isParkedOriginal && dragStarted(project?.id, project?.invoiceNo)}
          onContextMenu={handleContextMenu}
          className={`text-sm-2 min-h-6 flex relative
            ${isParkedOriginal
              ? "opacity-50 cursor-not-allowed"
              : "cursor-pointer hover:bg-skin-base-hover"
            }
            ${
              project.selected && "outline-solid -outline-offset-1 outline-yellow"
            } ${className}`}
          style={{
            ...style,
            // Phase 6 Diff Pair: 2px red outline when this tile differs
            // between T1 and T2. Inline (vs Tailwind) because the custom
            // palette's `red` (#FF8080) is too light to read as a "danger"
            // indicator; #dc2626 (red-600) reads correctly.
            ...(diff
              ? {
                  outline: `2px solid ${diff.changeKind === "removed" ? "#7f1d1d" : "#dc2626"}`,
                  outlineOffset: "-2px",
                  opacity: diffMuted ? 0.65 : 1,
                }
              : {}),
          }}
        >
          {selectable && (
            <div
              className="border border-transparent hover:border-[rgb(var(--color-fill-base))] rounded-full w-[20px] h-[20px] flex items-center justify-center absolute top-1 right-1 transition-colors"
              title="Details"
              onClick={(event) => {
                event.stopPropagation();
                return projectDetailClick(project, event);
              }}
            >
              <svg
                viewBox="0 0 16 16"
                xmlns="http://www.w3.org/2000/svg"
                className="w-[16px] h-[16px] fill-skin-base"
              >
                <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                <g
                  id="SVGRepo_tracerCarrier"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                ></g>
                <g id="SVGRepo_iconCarrier">
                  <path d="M9.5 13a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0z"></path>
                </g>
              </svg>
            </div>
          )}
          <div
            className={`bg-surface-${project?.surface
              ?.split(" ")
              .join("-")} badge`}
          ></div>
          {project?.installer === "" && project?.extraCellText
            ? project?.extraCellText.substring(0, 20)
            : ""}
          {largeView ? (
            <span className="mx-2 self-center font-medium whitespace-pre-line  first-line:font-bold">
              {renderFlagsWithIcons(project?.encodedLargeTitle)}
            </span>
          ) : tooltipText !== "" ? (
            <span className="mx-2 self-center font-medium">
              <span className="font-bold tooltip">
                {renderFlagsWithIcons(
                  project?.encodedSmallTitle?.substring(0, firstSpaceIndex)
                )}
                <span className="tooltiptext">{tooltipText}</span>
              </span>
              {project?.encodedSmallTitle?.substring(firstSpaceIndex)}
            </span>
          ) : (
            <span className="mx-2 self-center font-medium">
              <span className="font-bold">
                {renderFlagsWithIcons(
                  project?.encodedSmallTitle?.substring(0, firstSpaceIndex)
                )}
              </span>
              {project?.encodedSmallTitle?.substring(firstSpaceIndex)}
            </span>
          )}
          {diff && diff.events && diff.events.length > 0 && (
            <UserBubbleStack events={diff.events} muted={diffMuted} />
          )}
        </div>
      )}

      {/* Context menu for parking */}
      {contextMenu.visible && (
        <div
          ref={contextMenuRef}
          style={{
            position: "fixed",
            top: contextMenu.y,
            left: contextMenu.x,
            zIndex: 9999,
            backgroundColor: "white",
            border: "1px solid #d1d5db",
            borderRadius: "6px",
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
            padding: "4px 0",
            minWidth: "140px",
          }}
        >
          {parkAvailable && (
            <button
              onClick={handleParkClick}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                width: "100%",
                padding: "8px 16px",
                textAlign: "left",
                backgroundColor: "transparent",
                border: "none",
                cursor: "pointer",
                fontSize: "14px",
                color: "#111827",
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#f3f4f6"}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
            >
              {/* Parking icon */}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                style={{ fill: "#f97316", flexShrink: 0 }}
              >
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2c1.1 0 2 .9 2 2v2c0 1.1-.9 2-2 2h-1v3h-2V7h1zm1 4h1V9h-1v2z"/>
              </svg>
              <span>Park Job</span>
            </button>
          )}
          {/* SUP-9005 — Lock/Unlock in the SAME menu as Park. Disabled state +
              tooltip come from the shared buildLockMenuItem (matches the cell
              menu). */}
          {lockMenuItem && (
            <button
              onClick={handleLockClick}
              disabled={lockMenuItem.disabled}
              title={lockMenuItem.title || undefined}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                width: "100%",
                padding: "8px 16px",
                textAlign: "left",
                backgroundColor: "transparent",
                border: "none",
                cursor: lockMenuItem.disabled ? "not-allowed" : "pointer",
                fontSize: "14px",
                color: lockMenuItem.disabled ? "#9ca3af" : "#111827",
              }}
              onMouseEnter={(e) => {
                if (!lockMenuItem.disabled)
                  e.currentTarget.style.backgroundColor = "#f3f4f6";
              }}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
            >
              <span style={{ flexShrink: 0, fontSize: "14px", lineHeight: 1 }}>🔒</span>
              <span>{lockMenuItem.label}</span>
            </button>
          )}
        </div>
      )}
    </>
  );
};

export default JobTile;
